import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import NewsLayout from "../../components/news/NewsLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Loader2, PenLine, CheckCircle } from "lucide-react";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost, siteNameFromHost } from "../../lib/themeFromHost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const expertiseOptions = [
  "Business", "Finance", "Technology", "Politics", "Sports",
  "Entertainment", "Health", "Science", "Environment", "Travel"
];

const BecomeWriter = () => {
  const navigate = useNavigate();
  const { publicProject } = useProject();
  const theme = publicProject?.theme || themeFromHost();
  const siteName = publicProject?.name || siteNameFromHost() || undefined;
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    bio: "",
    portfolio_url: "",
    expertise: []
  });

  const toggleExpertise = (exp) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(exp)
        ? prev.expertise.filter(e => e !== exp)
        : [...prev.expertise, exp]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.expertise.length === 0) {
      toast.error("Please select at least one area of expertise");
      return;
    }

    setLoading(true);

    try {
      // Using a default project_id for demo
      await axios.post(`${API}/writer-applications?project_id=default`, formData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <NewsLayout theme={theme} siteName={siteName}>
        <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="application-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-4">
            Application Submitted!
          </h1>
          <p className="text-[#64748B] mb-8">
            Thank you for applying to become a writer. Our editorial team will review your application and get back to you soon.
          </p>
          <Button onClick={() => navigate("/")} data-testid="back-home-button">
            Back to Home
          </Button>
        </div>
      </NewsLayout>
    );
  }

  return (
    <NewsLayout theme={theme} siteName={siteName}>
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="become-writer-page">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#D97706] rounded-full flex items-center justify-center mx-auto mb-4">
            <PenLine className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">
            Become a Writer
          </h1>
          <p className="text-[#64748B]">
            Join our team of talented writers and share your stories with the world
          </p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Writer Application</CardTitle>
            <CardDescription>Fill out the form below to apply</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="writer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    data-testid="writer-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio / About You *</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell us about yourself, your writing experience, and why you want to write for us..."
                  rows={5}
                  required
                  data-testid="writer-bio-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio_url">Portfolio URL (optional)</Label>
                <Input
                  id="portfolio_url"
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  placeholder="https://yourportfolio.com"
                  data-testid="writer-portfolio-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Areas of Expertise *</Label>
                <p className="text-sm text-[#64748B] mb-3">Select topics you can write about</p>
                <div className="flex flex-wrap gap-2">
                  {expertiseOptions.map((exp) => (
                    <Badge
                      key={exp}
                      variant={formData.expertise.includes(exp) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        formData.expertise.includes(exp)
                          ? "bg-[#0F172A]"
                          : "hover:bg-[#F1F5F9]"
                      }`}
                      onClick={() => toggleExpertise(exp)}
                      data-testid={`expertise-${exp.toLowerCase()}`}
                    >
                      {exp}
                    </Badge>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0F172A] hover:bg-[#1E293B]"
                disabled={loading}
                data-testid="submit-application-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Application"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 p-6 bg-[#F8FAFC] rounded-lg">
          <h3 className="font-semibold text-[#0F172A] mb-3">What happens next?</h3>
          <ol className="list-decimal list-inside space-y-2 text-[#64748B]">
            <li>Our editorial team reviews your application</li>
            <li>If approved, you'll receive login credentials</li>
            <li>You can start submitting articles for review</li>
            <li>Approved articles get published on our platform</li>
          </ol>
        </div>
      </div>
    </NewsLayout>
  );
};

export default BecomeWriter;
